import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { formatPercentage } from '../../../utils/formatters';

const DEFAULT_COLORS = [
  '#1EC0B8',
  '#33C6BF',
  '#48CCC6',
  '#5DD2CD',
  '#72D8D4',
  '#86DEDB',
  '#9BE4E2',
  '#B0EAE8',
  '#C4F0EF',
  '#D8F6F5',
];

export const getChartColors = (total) => {
  if (!total || total <= 0) return [];

  if (total === 1) {
    return ['#0EAEA6'];
  }

  if (total >= DEFAULT_COLORS.length) {
    return DEFAULT_COLORS;
  }

  const lastIndex = DEFAULT_COLORS.length - 1;

  return Array.from({ length: total }, (_, index) => {
    const colorIndex = Math.round((index * lastIndex) / (total - 1));
    return DEFAULT_COLORS[colorIndex];
  });
};

const RAD = Math.PI / 180;

function PillLabel({ cx, cy, midAngle, outerRadius, name }) {
  const radius = outerRadius + 12;
  const x = cx + radius * Math.cos(-midAngle * RAD);
  const y = cy + radius * Math.sin(-midAngle * RAD);

  const paddingX = 8;
  const charW = 7;
  const textWidth = (name ? name.length : 0) * charW;
  const pillW = textWidth + paddingX * 2;
  const pillH = 24;

  const rectX = x - pillW / 2;
  const rectY = y - pillH / 2;

  return (
    <g>
      <rect x={rectX} y={rectY} width={pillW} height={pillH} rx={pillH / 5} fill="#fff" />
      <text
        x={x}
        y={y + 1}
        fill="#143145"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        {name}
      </text>
    </g>
  );
}

export default function DonutChart3({
  data,
  valueKey = 'percent',
  nameKey = 'label',
  amountKey = 'amount',
  colors = getChartColors(data ? data.length : 0),
  height = 210,
  showLegend = true,
}) {
  const safeData = useMemo(
    () =>
      (data || []).map((d) => ({
        ...d,
        [valueKey]: Number(d[valueKey]) || 0,
      })),
    [data, valueKey]
  );

  const formatCurrency = (value) => {
    const number = Number(value) || 0;

    if (number >= 1000000) {
      return `R$ ${(number / 1000000).toLocaleString('pt-BR', {
        maximumFractionDigits: 1,
      })}M`;
    }

    if (number >= 1000) {
      return `R$ ${(number / 1000).toLocaleString('pt-BR', {
        maximumFractionDigits: 1,
      })}K`;
    }

    return `R$ ${number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="w-full">
      <div className="w-full" style={{ height }}>
        <ResponsiveContainer>
          <PieChart>
            <Tooltip
              wrapperStyle={{ zIndex: 9999 }}
              formatter={(value, name) => {
                return [`${value}%`, name];
              }}
            />

            <Pie
              data={safeData}
              dataKey={valueKey}
              nameKey={nameKey}
              innerRadius="40%"
              startAngle={90}
              endAngle={-270}
              labelLine={false}
              label={(props) => <PillLabel {...props} name={props.name} />}
              stroke="none"
            >
              {safeData.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      {showLegend && (
        <div className="mt-4 space-y-2">
          {safeData.map((item, i) => {
            const label = item[nameKey];
            const percent = item[valueKey];
            const amount = item[amountKey];

            return (
              <div key={i} className="flex items-center text-base font-jost">
                <span
                  className="inline-block w-3 h-3 rounded mr-3 flex-shrink-0"
                  style={{ backgroundColor: colors[i % colors.length] }}
                />

                <span>
                  <span className="font-semibold">{label}:</span>{' '}
                  {`${formatPercentage(percent, 0) == '0%' ? '~' : ''} ${formatPercentage(percent, 0)}`}
                  {amount !== undefined && amount !== null && <> ({formatCurrency(amount)})</>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
